import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { Button, Input, InputNumber, Table, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { BodyText, H3, H4 } from 'src/branding/typography'
import OrderFinancialBreakdown from 'src/components/shared/OrderFinancialBreakdown'
import {
  fetchApprovalDetail,
  formatApprovalTimestamp,
  submitApprovalDecision,
} from 'src/utils/dashboardApprovalsApi'

const { TextArea } = Input

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
`

const MetaLabel = styled(BodyText)`
  font-weight: 700;
  margin: 0;
`

const MetaValue = styled(BodyText)`
  margin: 0;
`

const Totals = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin: ${({ theme }) => `${theme.spacing.lg} 0`};
`

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};

  ${({ theme }) => theme.device('sm')`
    flex-direction: row;
    justify-content: center;
  `};
`

const DeclineButton = styled(Button)`
  background-color: ${({ theme }) => theme.colours.chilliRed};
  border-color: ${({ theme }) => theme.colours.chilliRed};
  color: white;
  height: 46px;
  font-weight: 600;
  flex: 1;

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colours.chilliRed};
    border-color: ${({ theme }) => theme.colours.chilliRed};
    color: white;
    opacity: 0.9;
  }
`

const ApproveButton = styled(Button)`
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  border-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: white;
  height: 46px;
  font-weight: 600;
  flex: 1;

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colours.lettuceGreen};
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    color: white;
    opacity: 0.9;
  }
`

const ProcessedBanner = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl} 0;
`

const recalculateTotals = items => {
  let subtotal = 0
  let vatTotal = 0

  for (const item of items) {
    const quantity = Number(item.quantity) || 0
    const unitPrice = Number(item.unitPrice) || 0
    const lineTotal = quantity * unitPrice
    const lineVat = item.hasVat ? lineTotal * 0.2 : 0
    subtotal += lineTotal
    vatTotal += lineVat
  }

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    vat: parseFloat(vatTotal.toFixed(2)),
    total: parseFloat((subtotal + vatTotal).toFixed(2)),
  }
}

const DECISION_COPY = {
  approved: {
    title: 'Order approved',
    body: 'This order has been approved and sent to the supplier.',
  },
  rejected: {
    title: 'Order declined',
    body: 'This order has been declined and will not be sent to the supplier.',
  },
  auto_rejected: {
    title: 'Order expired',
    body: 'This order was automatically rejected because it expired without approval.',
  },
}

/**
 * Approval detail for one batch, shown inside a drawer on the Approvals page.
 * Same decision workflow as the emailed approval link (quantities can be
 * amended before approving), but authenticated against the signed-in manager.
 */
const ApprovalDetail = ({ venueId, token, onDecided, onUnavailable }) => {
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState(null)
  const [managerMessage, setManagerMessage] = useState('')
  const [decision, setDecision] = useState(null)
  // Editable quantities keyed by orderId (manager amend-before-approve)
  const [editedQuantities, setEditedQuantities] = useState({})

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchApprovalDetail({ venueId, token })
      setDetail(data)

      const initialQuantities = {}
      ;(data.items || []).forEach(item => {
        const id = item.orderId != null ? String(item.orderId) : null
        if (id) {
          initialQuantities[id] = Number(item.quantity) || 0
        }
      })
      setEditedQuantities(initialQuantities)

      if (data.isAlreadyProcessed && data.approvalStatus) {
        setDecision(data.approvalStatus)
      }
    } catch (err) {
      if (onUnavailable) {
        onUnavailable()
        return
      }
      setError(
        err?.message || 'Failed to load approval details. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }, [venueId, token, onUnavailable])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  const editableItems = useMemo(() => {
    if (!detail?.items) return []

    return detail.items.map(item => {
      const orderId = item.orderId != null ? String(item.orderId) : null
      const originalQuantity =
        item.originalQuantity != null
          ? Number(item.originalQuantity)
          : Number(item.quantity) || 0
      const quantity =
        orderId && editedQuantities[orderId] != null
          ? Number(editedQuantities[orderId])
          : Number(item.quantity) || 0

      return {
        ...item,
        orderId,
        originalQuantity,
        quantity,
        total: quantity * (Number(item.unitPrice) || 0),
        isChanged: quantity !== originalQuantity,
        isRemoved: quantity === 0,
      }
    })
  }, [detail, editedQuantities])

  const liveTotals = useMemo(
    () => recalculateTotals(editableItems),
    [editableItems],
  )

  const hasQuantityChanges = useMemo(
    () => editableItems.some(item => item.isChanged),
    [editableItems],
  )

  const remainingItemCount = useMemo(
    () => editableItems.filter(item => item.quantity > 0).length,
    [editableItems],
  )

  const handleQuantityChange = (orderId, value) => {
    if (!orderId) return
    const next =
      value == null || value === '' ? 0 : Math.max(0, Number(value) || 0)
    setEditedQuantities(prev => ({ ...prev, [orderId]: next }))
  }

  const handleDecision = async action => {
    try {
      if (managerMessage.length > 1000) {
        message.error('Message to requester must be 1000 characters or fewer.')
        return
      }

      if (action === 'approve' && remainingItemCount === 0) {
        message.error(
          'Cannot approve an order with no remaining products. Decline the order instead.',
        )
        return
      }

      setProcessing(true)
      const response = await submitApprovalDecision({
        venueId,
        token,
        action,
        message: managerMessage,
        items: action === 'approve' ? editableItems : null,
      })

      setDecision(action === 'approve' ? 'approved' : 'rejected')
      message.success(response?.message || 'Decision saved.')
      if (onDecided) {
        onDecided({ action, purchaseOrderNumber: detail?.purchaseOrderNumber })
      }
    } catch (err) {
      // Already-processed / expired approvals surface the server's reason
      message.error(
        err?.message || `Failed to ${action} the order. Please try again.`,
      )
      // A duplicate decision means state moved under us — refresh the detail
      if (
        err?.code === 'ORDER_ALREADY_PROCESSED' ||
        err?.code === 'ORDER_AUTO_REJECTED' ||
        err?.code === 'APPROVAL_LINK_EXPIRED'
      ) {
        loadDetail()
      }
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return <BodyText>Loading approval details…</BodyText>
  }

  if (error) {
    return (
      <ProcessedBanner>
        <ExclamationCircleOutlined
          style={{ fontSize: 42, color: '#fa8c16', marginBottom: 12 }}
        />
        <H3>{error}</H3>
      </ProcessedBanner>
    )
  }

  if (decision) {
    const copy = DECISION_COPY[decision] || DECISION_COPY.approved
    return (
      <ProcessedBanner>
        {decision === 'approved' ? (
          <CheckCircleOutlined
            style={{ fontSize: 48, color: '#52c41a', marginBottom: 12 }}
          />
        ) : (
          <CloseCircleOutlined
            style={{ fontSize: 48, color: '#dc3545', marginBottom: 12 }}
          />
        )}
        <H3>{copy.title}</H3>
        <BodyText>{copy.body}</BodyText>
      </ProcessedBanner>
    )
  }

  if (!detail) return null

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productTitle',
      key: 'productTitle',
      render: (title, record) => (
        <span
          style={
            record.isRemoved
              ? { textDecoration: 'line-through', color: '#999' }
              : undefined
          }
        >
          {title}
          {record.isChanged && !record.isRemoved && (
            <span style={{ marginLeft: 8, color: '#d48806', fontSize: 12 }}>
              (was {record.originalQuantity})
            </span>
          )}
          {record.isRemoved && (
            <span style={{ marginLeft: 8, color: '#cf1322', fontSize: 12 }}>
              removed
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      width: 110,
      render: (quantity, record) => (
        <InputNumber
          min={0}
          value={quantity}
          disabled={processing}
          onChange={value => handleQuantityChange(record.orderId, value)}
          style={{
            width: 80,
            backgroundColor: record.isChanged ? '#fffbe6' : undefined,
          }}
        />
      ),
    },
    {
      title: 'Unit price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      responsive: ['sm'],
      render: (price, record) =>
        `£${parseFloat(price || 0).toFixed(2)}${
          record?.hasVat ? ' + VAT' : ''
        }`,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: total => `£${parseFloat(total || 0).toFixed(2)}`,
    },
    {
      title: '',
      key: 'remove',
      align: 'center',
      width: 60,
      responsive: ['sm'],
      render: (_, record) => (
        <Button
          type='link'
          danger
          icon={<DeleteOutlined />}
          disabled={processing || record.quantity === 0}
          onClick={() => handleQuantityChange(record.orderId, 0)}
        />
      ),
    },
  ]

  return (
    <div>
      <DetailHeader>
        <H3 style={{ margin: 0 }}>
          {detail.supplier?.title || 'Order awaiting approval'}
        </H3>
        {detail.purchaseOrderNumber && (
          <BodyText>PO number: {detail.purchaseOrderNumber}</BodyText>
        )}
      </DetailHeader>

      <MetaGrid>
        <MetaItem>
          <MetaLabel>Requested by</MetaLabel>
          <MetaValue>{detail.requestedBy?.trim() || '—'}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Delivery</MetaLabel>
          <MetaValue>
            {detail.requiredDeliveryDay
              ? new Date(detail.requiredDeliveryDay).toLocaleDateString(
                  'en-GB',
                  {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  },
                )
              : 'Next available delivery day'}
          </MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Expires</MetaLabel>
          <MetaValue>{formatApprovalTimestamp(detail.expiresAt)}</MetaValue>
        </MetaItem>
        {detail.notes && (
          <MetaItem>
            <MetaLabel>Order notes</MetaLabel>
            <MetaValue>{detail.notes}</MetaValue>
          </MetaItem>
        )}
      </MetaGrid>

      <Table
        columns={columns}
        dataSource={editableItems.map((item, index) => ({
          ...item,
          key: item.orderId || index,
        }))}
        pagination={false}
        size='small'
        rowClassName={record =>
          record.isChanged ? 'approval-qty-changed' : ''
        }
      />

      <Totals>
        <OrderFinancialBreakdown
          conflict={detail.financialSummary?.hasConflict}
          orders={
            detail.orderFinancialSnapshot
              ? [{ orderFinancialSnapshot: detail.orderFinancialSnapshot }]
              : []
          }
          context={{
            venueId: detail.venue?.id || venueId,
            supplierId: detail.supplier?.id,
            purchaseOrderNumber: detail.purchaseOrderNumber,
          }}
        />
        <TotalRow>
          <BodyText>
            <b>
              {detail.orderFinancialSnapshot ? 'Goods ex VAT' : 'Total ex VAT'}
            </b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.subtotal.toFixed(2)}</b>
          </BodyText>
        </TotalRow>
        <TotalRow>
          <BodyText>
            <b>{detail.orderFinancialSnapshot ? 'Goods VAT' : 'VAT'}</b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.vat.toFixed(2)}</b>
          </BodyText>
        </TotalRow>
        <TotalRow>
          <BodyText>
            <b>{detail.orderFinancialSnapshot ? 'Goods incl VAT' : 'Total'}</b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.total.toFixed(2)}</b>
          </BodyText>
        </TotalRow>
      </Totals>

      <div>
        <H4>Message to requester (optional)</H4>
        <TextArea
          rows={3}
          value={managerMessage}
          onChange={e => setManagerMessage(e.target.value)}
          maxLength={1000}
          placeholder='Add context for the requester (e.g. Please use main supplier unless out of stock)'
        />
        <BodyText
          style={{
            marginTop: 4,
            fontSize: 12,
            textAlign: 'right',
            color: '#999',
          }}
        >
          {managerMessage.length}/1000 characters
        </BodyText>
      </div>

      {hasQuantityChanges && (
        <BodyText style={{ marginTop: 12, color: '#d48806' }}>
          You have adjusted one or more quantities. The amended order will be
          sent to the supplier.
        </BodyText>
      )}

      <ActionRow>
        <DeclineButton
          icon={<CloseCircleOutlined />}
          onClick={() => handleDecision('decline')}
          loading={processing}
          disabled={processing}
        >
          Decline order
        </DeclineButton>
        <ApproveButton
          icon={<CheckCircleOutlined />}
          onClick={() => handleDecision('approve')}
          loading={processing}
          disabled={processing || remainingItemCount === 0}
        >
          {hasQuantityChanges ? 'Approve adjusted order' : 'Approve order'}
        </ApproveButton>
      </ActionRow>

      <BodyText
        style={{ textAlign: 'center', color: '#ff9800', marginTop: '16px' }}
      >
        ⚠️ If no action is taken within 48 hours of submission, this order will
        be automatically rejected.
      </BodyText>
    </div>
  )
}

export default ApprovalDetail
